import { memo, useMemo } from 'react';
import { Cell } from './Cell';
import { BOARD_SIZE, BOX_SIZE, COLORS } from '@/game/configs';
import type { BoardState } from '@/utils/sudokuGenerator';
interface SudokuBoardProps {
  board: BoardState;
  selectedCell: number | null;
  onCellSelect: (index: number) => void;
}
export const SudokuBoard = memo(function SudokuBoard({
  board,
  selectedCell,
  onCellSelect,
}: SudokuBoardProps) {
  const { highlightedCells, errorCells } = useMemo(() => {
    const highlighted = new Set<number>();
    const errors = new Set<number>();
    if (selectedCell !== null) {
      const selectedRow = Math.floor(selectedCell / BOARD_SIZE);
      const selectedCol = selectedCell % BOARD_SIZE;
      const selectedBox =
        Math.floor(selectedRow / BOX_SIZE) * BOX_SIZE +
        Math.floor(selectedCol / BOX_SIZE);
      // Highlight row, column, and box
      for (let i = 0; i < BOARD_SIZE * BOARD_SIZE; i++) {
        const row = Math.floor(i / BOARD_SIZE);
        const col = i % BOARD_SIZE;
        const box =
          Math.floor(row / BOX_SIZE) * BOX_SIZE +
          Math.floor(col / BOX_SIZE);
        if (
          row === selectedRow ||
          col === selectedCol ||
          box === selectedBox
        ) {
          highlighted.add(i);
        }
      }
      // Find error cells (same value in same row/col/box)
      const selectedValue = board[selectedCell].value;
      if (selectedValue !== null) {
        for (let i = 0; i < BOARD_SIZE * BOARD_SIZE; i++) {
          if (i !== selectedCell && board[i].value === selectedValue) {
            const row = Math.floor(i / BOARD_SIZE);
            const col = i % BOARD_SIZE;
            const box =
              Math.floor(row / BOX_SIZE) * BOX_SIZE +
              Math.floor(col / BOX_SIZE);
            const sRow = Math.floor(selectedCell / BOARD_SIZE);
            const sCol = selectedCell % BOARD_SIZE;
            const sBox =
              Math.floor(sRow / BOX_SIZE) * BOX_SIZE +
              Math.floor(sCol / BOX_SIZE);
            if (
              row === sRow ||
              col === sCol ||
              box === sBox
            ) {
              errors.add(i);
            }
          }
        }
      }
    }
    return { highlightedCells: highlighted, errorCells: errors };
  }, [board, selectedCell]);
  const getBorderClass = (index: number): string => {
    const row = Math.floor(index / BOARD_SIZE);
    const col = index % BOARD_SIZE;
    const rightBorder =
      col === BOARD_SIZE - 1
        ? 'border-r-2 border-r-[#8B7355]'
        : (col + 1) % BOX_SIZE === 0
        ? 'border-r-2 border-r-[#8B7355]'
        : 'border-r border-r-[#D4C4B0]';
    const bottomBorder =
      row === BOARD_SIZE - 1
        ? 'border-b-2 border-b-[#8B7355]'
        : (row + 1) % BOX_SIZE === 0
        ? 'border-b-2 border-b-[#8B7355]'
        : 'border-b border-b-[#D4C4B0]';
    return `${rightBorder} ${bottomBorder}`;
  };
  const handleCellClick = (index: number) => {
    // Prevent selection of given cells - they are read-only
    if (board[index].isGiven) {
      return;
    }
    onCellSelect(index);
  };
  return (
    <div
      className="grid w-full max-w-[360px] mx-auto rounded-2xl p-2 sm:p-3"
      style={{
        backgroundColor: COLORS.ivory,
        boxShadow: '8px 8px 16px rgba(139, 115, 85, 0.25), -8px -8px 16px rgba(255, 255, 255, 0.9)',
        gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)`,
      }}
    >
      {board.map((cell, index) => (
        <div
          key={index}
          className={`p-px sm:px-0.5 ${getBorderClass(index)}`}
        >
          <Cell
            value={cell.value}
            notes={cell.notes}
            isGiven={cell.isGiven}
            isSelected={selectedCell === index}
            isHighlighted={highlightedCells.has(index)}
            isError={errorCells.has(index)}
            onClick={() => handleCellClick(index)}
          />
        </div>
      ))}
    </div>
  );
});